import React from 'react';

import { observer } from 'mobx-react';
import { Skeleton, Switch, Typography } from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';

import { Button } from 'components/Button';

import Record from 'ui/Record/Record';
import MainHeader from 'components/MainHeader';
import { useStore } from 'utils/hooks/store';
import { useNavBarHeight } from 'utils/hooks/navbar';
import routes from 'core/routes';

import {
  CHANNEL_TYPES,
  getCategoryWorkflowItems,
  getVisibleCategories,
  getWorkflowDefault,
  WorkflowItem,
  WorkflowKey,
} from 'stores/KnockPreferences/config';

import {
  Container,
  Header,
  Grid,
  GridHeaderRow,
  HeaderSpacer,
  CategorySection,
  CategoryHeaderRow,
  CategoryInfo,
  CategoryTextGroup,
  CategoryLabel,
  CategoryDescription,
  WorkflowsContainer,
  WorkflowRow,
  WorkflowCell,
  WorkflowTextGroup,
  WorkflowLabel,
  WorkflowDescription,
  ChannelHeaderCell,
  ChannelCell,
  ErrorContainer,
} from './styles';

const KnockPreferenceCenter = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const {
    session,
    knockPreferences: {
      loadPreferences,
      isLoading,
      isLoaded,
      isFailure,
      isDisabled,
      isSaving,
      preferences,
      updateWorkflowChannel,
    },
  } = useStore();
  const userRole = session.user?.role;
  const visibleCategories = session.user
    ? getVisibleCategories(session.user.licenses, session.user.activeEntityModules)
    : [];
  const documentAcknowledgementsEnabled = session.user?.documentAcknowledgementsEnabled ?? false;
  const risksAssessmentsModuleEnabled = session.user?.risksAssessmentsModuleEnabled ?? false;
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set());

  const renderWorkflowItem = (item: WorkflowItem): React.ReactElement => (
    <WorkflowRow key={item.key}>
      <WorkflowCell>
        <WorkflowTextGroup>
          <WorkflowLabel>{item.label}</WorkflowLabel>
          {item.description && <WorkflowDescription>{item.description}</WorkflowDescription>}
        </WorkflowTextGroup>
      </WorkflowCell>
      {CHANNEL_TYPES.map((channel) => {
        if (item.channels && !item.channels.includes(channel.key)) {
          return <ChannelCell key={channel.key} />;
        }
        if (isLoading) {
          return (
            <ChannelCell key={channel.key}>
              <Skeleton variant="rectangular" width={34} height={20} sx={{ borderRadius: '10px' }} />
            </ChannelCell>
          );
        }
        const checked = preferences[item.key]?.[channel.key] ?? getWorkflowDefault(item.key, userRole);
        return (
          <ChannelCell key={channel.key}>
            <Switch
              checked={checked}
              onChange={(_, c) => updateWorkflowChannel(item.key, channel.key, c)}
              disabled={isSaving}
              size="small"
            />
          </ChannelCell>
        );
      })}
    </WorkflowRow>
  );

  React.useEffect(() => {
    if (!isLoading && !isLoaded && !isFailure) {
      loadPreferences();
    }
  }, [isLoading, isLoaded, isFailure, loadPreferences]);

  const toggleCategory = (categoryKey: string): void => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  return (
    <Record
      Header={
        <MainHeader
          title="Notification preferences"
          breadcrumbs={[
            {
              label: 'Personal',
              link: '',
            },
            {
              label: 'Notifications',
              link: routes.dashboard.settings.notifications.toString(),
            },
          ]}
        />
      }
      Content={() => (
        <Container>
          <Header>
            <h1>Notification preferences</h1>
            <p>Choose how you want to be notified for each product area</p>
          </Header>
          {isDisabled ? (
            <ErrorContainer>
              <Typography color="text.secondary">
                Notification preferences aren&apos;t available for your organisation. Contact your administrator to turn
                them on.
              </Typography>
            </ErrorContainer>
          ) : isFailure ? (
            <ErrorContainer>
              <Typography color="text.secondary">We couldn&apos;t load your notification preferences</Typography>
              <Button variant="outlined" size="small" onClick={loadPreferences}>
                Try again
              </Button>
            </ErrorContainer>
          ) : (
            <Grid>
              <GridHeaderRow>
                <HeaderSpacer />
                {CHANNEL_TYPES.map((channel) => (
                  <ChannelHeaderCell key={channel.key}>{channel.label}</ChannelHeaderCell>
                ))}
              </GridHeaderRow>
              {visibleCategories.map((category) => (
                <CategorySection key={category.key}>
                  <CategoryHeaderRow onClick={() => toggleCategory(category.key)}>
                    <CategoryInfo>
                      {expandedCategories.has(category.key) ? (
                        <ExpandMore color="action" />
                      ) : (
                        <ChevronRight color="action" />
                      )}
                      <CategoryTextGroup>
                        <CategoryLabel>{category.label}</CategoryLabel>
                        <CategoryDescription>{category.description}</CategoryDescription>
                      </CategoryTextGroup>
                    </CategoryInfo>
                  </CategoryHeaderRow>
                  {expandedCategories.has(category.key) && (
                    <WorkflowsContainer>
                      {getCategoryWorkflowItems(category, userRole, {
                        [WorkflowKey.acknowledgementAssigned]: documentAcknowledgementsEnabled,
                        [WorkflowKey.riskActionAssigned]: risksAssessmentsModuleEnabled,
                      }).map(renderWorkflowItem)}
                    </WorkflowsContainer>
                  )}
                </CategorySection>
              ))}
            </Grid>
          )}
        </Container>
      )}
      navBarHeight={navBarHeight}
    />
  );
};

export default observer(KnockPreferenceCenter);
